import { colors, sizes } from '@lmc/common/constants'
import { EmptyState, NoDataIcon } from '@lmc/ui-kit'
import dayjs from 'dayjs'
import _ from 'lodash'
import PropTypes from 'prop-types'
import { Component } from 'react'
import { connect } from 'react-redux'
import { LinkComponent } from '../../../../components/generic'
import { history } from '../../../../store'
import withDataLoader from '../withDataLoader'

const startOfDay = (d) => dayjs(d).startOf('day')

const DATE_FORMAT = 'Do'
const MONTH_FORMAT = 'MMMM'

class DailyTotalTable extends Component {
  formatLogs(rows, residents) {
    return _(rows)
      .map((row) => ({
        resident: _.find(residents, { id: row.resident }),
        data: row.data.map(({ date, value }) => ({
          date: dayjs(date, 'DD/MM/YYYY').format(),
          value
        }))
      }))
      .orderBy('resident.name.first')
      .value()
  }

  handleClick(link, residentId) {
    history.push(`/residents/${residentId}/charts/${link}`)
  }

  getMonths(days) {
    return _.chain(days)
      .map((d) => dayjs(d).startOf('month').format())
      .countBy()
      .map((count, date) => ({ date: dayjs(date), count }))
      .value()
  }

  getHeatmapStyles(value, heatmapRanges) {
    if (!heatmapRanges) return null
    let background
    if (!value) {
      background = 'rgba(0,0,0,0)'
    } else if (value < heatmapRanges.low) {
      background = 'rgba(0,0,0,0.02)'
    } else if (value < heatmapRanges.medium) {
      background = 'rgba(0,0,0,0.06)'
    } else if (value < heatmapRanges.high) {
      background = 'rgba(0,0,0,0.1)'
    } else if (value < heatmapRanges.veryHigh) {
      background = 'rgba(0,0,0,0.14)'
    } else if (value >= heatmapRanges.veryHigh) {
      background = 'rgba(0,0,0,0.18)'
    }
    return { background }
  }

  getBorderStyle = (d, index, days) => {
    if (index === days.length - 1) {
      return { borderRight: `2px solid ${colors.grey}` }
    }
    if (index === 0 || d.format('DD') === '01') {
      return { borderLeft: `2px solid ${colors.grey}` }
    }
  }

  renderHeaderDay = (day, index, days) => (
    <th style={this.getBorderStyle(day, index, days)} key={day.format()}>
      {day.format(days.length > 20 ? 'D' : DATE_FORMAT)}
    </th>
  )

  renderHeader = (daysIndex) => {
    const { from, displayResident = true } = this.props
    const days = daysIndex.map((d) => startOfDay(from).add(d, 'd'))
    const months = this.getMonths(days)

    return (
      <thead className="lmc-table-center-text">
        <tr>
          {displayResident && <th style={styles.nameTh} />}
          {months.map((month) => (
            <th
              key={month.date.format()}
              colSpan={month.count}
              style={styles.monthRow}
            >
              {month.date.format(MONTH_FORMAT)}
            </th>
          ))}
        </tr>
        <tr>
          {displayResident && <th />}
          {days.map((d, i) => this.renderHeaderDay(d, i, days))}
        </tr>
      </thead>
    )
  }

  renderValue = (day, row) => {
    const { from, link, heatmapRanges } = this.props
    const data = _.keyBy(row.data, 'date')
    const _date = startOfDay(from)
      .add(day || 0, 'd')
      .format()
    let value = _.get(data, `${_date}.value`)

    if (value) value = parseFloat(value.toFixed(1))

    const tdStyle = this.getHeatmapStyles(value, heatmapRanges)

    return (
      <td
        key={day}
        style={{ ...styles.valueTd, ...tdStyle }}
        onClick={() => this.handleClick(link, row.resident.id)}
        className="lmc-table-td__selectable"
      >
        {value ? (
          <span style={styles.value}>{value}</span>
        ) : (
          <span style={styles.zeroValue}>0</span>
        )}
      </td>
    )
  }

  renderRow = (row, days) => {
    const { displayResident = true } = this.props
    if (!row.resident) return null
    return (
      <tr key={row.resident.id}>
        {displayResident && (
          <td style={{ borderRight: `2px solid ${colors.grey}`, padding: 0 }}>
            <LinkComponent
              style={{ color: 'black' }}
              to={`/residents/${row.resident.id}/profile`}
            >
              <span
                style={{
                  height: 30,
                  width: 30,
                  marginRight: 9,
                  display: 'inline-block'
                }}
              >
                <img
                  width={30}
                  height={30}
                  src={row.resident.picture}
                  alt=""
                  style={{ borderRadius: 15, position: 'relative', top: -2 }}
                />
              </span>
              <span
                style={{
                  display: 'inline-block',
                  marginTop: 15,
                  marginBottom: 15
                }}
              >
                {row.resident.name.first} {row.resident.name.last[0]}
              </span>
            </LinkComponent>
          </td>
        )}
        {days.map((day) => this.renderValue(day, row))}
      </tr>
    )
  }

  render() {
    const { data, residents, to, from, tableStyle } = this.props
    const rows = this.formatLogs(data, residents)

    const diff = to.diff(from, 'days') + 1
    const days = _.range(0, diff)

    if (!rows || !rows.length) {
      return (
        <EmptyState
          title="Shoot... No data to show you"
          icon={<NoDataIcon />}
        />
      )
    }

    return (
      <table
        className="Table ItemList"
        style={{ ...styles.table, ...tableStyle }}
      >
        {this.renderHeader(days)}
        <tbody>{rows.map((row) => this.renderRow(row, days))}</tbody>
      </table>
    )
  }
}

DailyTotalTable.propTypes = {
  from: PropTypes.string.isRequired,
  to: PropTypes.string.isRequired
}

const styles = {
  table: {
    minWidth: 1200
  },
  nameTh: {
    width: 180,
    border: 'none'
  },
  blackSlate: {
    margin: 40
  },
  zeroValue: {
    opacity: 0.2
  },
  valueTd: {
    cursor: 'pointer',
    textAlign: 'center',
    fontSize: 12
  },
  value: {
    opacity: 0.8
  },
  monthRow: {
    color: 'rgba(0,0,0,0.8)',
    padding: '10px 5px 2px 5px',
    fontWeight: 'bold',
    borderBottom: 'none',
    borderTop: `2px solid ${colors.grey}`,
    borderRight: `2px solid ${colors.grey}`,
    borderLeft: `2px solid ${colors.grey}`
  },
  blankStateWrapper: {
    maxWidth: sizes.childScreen,
    marginLeft: 'auto',
    marginRight: 'auto'
  }
}

const mapStateToProps = (state) => ({
  residents: state.data.residents
})

const _DailyTotalTable = connect(mapStateToProps)(DailyTotalTable)

export default withDataLoader(_DailyTotalTable, {
  errorMessage: 'Nothing to see here',
  url: ({ to, from, type }) => {
    let url = `/api/charts/${type}/dailyTotal`
    // possible to just pass a params object?
    if (from && to) {
      url += `?to=${to.toISOString()}&from=${from.toISOString()}`
    } else if (to) {
      url += `?to=${to.toISOString()}`
    } else if (from) {
      url += `?from=${from.toISOString()}`
    }

    return url
  }
})
